/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useMemo } from "react";
import NewsItem from "./NewsItem";
import { OverlayTrigger, Popover } from "react-bootstrap";
import NewsItemSkeleton from "./NewsItemSkeleton";
import NewsFeedSkeleton from "./NewsFeedSkeleton";
import { debounce } from "lodash";
import { handleNewsClick } from "./NewsFeedUtils";
import NewsDetailDiv from "./NewsDetailDiv";
import InfoFlowPanel from "./InfoFlowPanel";
import { capitalize } from "../DashboardUtils";
import { useResolvedDark } from "../../../ThemeProvider/ThemeContext";

const InfoPopover = (
  <Popover id="info-popover">
    <Popover.Body>
      <div>
        <i className="fa-solid fa-caret-up fa-lg me-2" style={{ color: "green" }} />{" "}
        High Relevance
      </div>
      <div>
        <i className="fa-solid fa-circle me-2 fa-sm" style={{ color: "#ffc107" }} />{" "}
        Moderate Relevance
      </div>
      <div>
        <i className="fa-solid fa-caret-down me-2 fa-lg" style={{ color: "red" }} />{" "}
        Low Relevance
      </div>
    </Popover.Body>
  </Popover>
);

const InfoButton = ({ color }) => (
  <OverlayTrigger trigger={["hover", "focus"]} overlay={InfoPopover}>
    <i className="fa-solid fa-circle-info" style={{ color }} />
  </OverlayTrigger>
);

function NewsFeed({
  newsList,
  isNewsLoading,
  noMoreNews,
  setDocumentIds,
  documentIds,
  handleLoadMoreNews,
  isMoreNewsLoading,
  ner_relevance_cutoff,
  selectedNews,
  setSelectedNews,
  resetDate,
  selectedPoint,
  selectedDate,
  isDark: isDarkProp,
  // "desk" renders the desktop CoTrader-style panel; paging, sorting and
  // relevance filtering stay shared with the mobile list below.
  variant,
  isFocused = false,
}) {
  const isDark = useResolvedDark(isDarkProp);
  const isMobile = window.innerWidth < 768;
  const theme = isDark
    ? {
        bg: "#0B1020",
        surface: "#0F1424",
        surface2: "#121A2E",
        border: "#232B4A",
        text: "#E8ECF8",
        muted: "#A6B0CF",
        pillBg: "#141C33",
        pillBorder: "#2A3560",
      }
    : {
        bg: "#FFFFFF",
        surface: "#FFFFFF",
        surface2: "#FAFAFA",
        border: "#E9ECEF",
        text: "#0B1220",
        muted: "#5B6472",
        pillBg: "#F8F9FA",
        pillBorder: "#DEE2E6",
      };

  const [prevDocumentIds, setPrevDocumentIds] = useState(null);
  const [apiCall, setApiCall] = useState(0);
  const rowRef = useRef(null);
  const [hover, setHover] = useState(false);

  const newsArticles = useMemo(() => {
    setSelectedNews(null);
    const sortedNews = newsList.sort((a, b) => {
      const dateComparison =
        new Date(b.data_timestamp) - new Date(a.data_timestamp);
      if (
        dateComparison === 0 &&
        a.ner_relevance_score &&
        b.ner_relevance_score
      ) {
        return b.ner_relevance_score - a.ner_relevance_score;
      } else {
        return dateComparison;
      }
    });

    const filteredNews = sortedNews.filter(
      (article) => article.ner_relevance_score > ner_relevance_cutoff
    );

    if (filteredNews.length === 0) {
      const allNullScores = sortedNews.every(
        (article) => article.ner_relevance_score === null
      );
      if (allNullScores) return [];
      return [];
    }

    return filteredNews;
  }, [newsList]);

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = rowRef.current;
    if (scrollTop + clientHeight >= scrollHeight && !selectedNews) {
      setApiCall((currentApiCall) => currentApiCall + 1);
    }
  };

  const debouncedHandleScroll = useRef(debounce(handleScroll, 300)).current;

  useEffect(() => {
    const currentRowRef = rowRef.current;
    if (currentRowRef) {
      currentRowRef.addEventListener("scroll", debouncedHandleScroll);
    }
    return () => {
      if (currentRowRef) {
        currentRowRef.removeEventListener("scroll", debouncedHandleScroll);
      }
    };
  }, [debouncedHandleScroll]);

  useEffect(() => {
    if (apiCall !== 0 && !noMoreNews && !selectedNews) {
      handleLoadMoreNews();
    }
  }, [apiCall]);

  const onClose = () => {
    setSelectedNews(null);
    setDocumentIds(prevDocumentIds);
  };

  if (variant === "desk") {
    return (
      <InfoFlowPanel
        articles={newsArticles}
        isLoading={isNewsLoading}
        isMoreLoading={isMoreNewsLoading}
        selectedNews={selectedNews}
        onOpen={(news) =>
          handleNewsClick(
            setSelectedNews,
            news,
            documentIds,
            setPrevDocumentIds,
            setDocumentIds
          )
        }
        onClose={onClose}
        selectedPoint={selectedPoint}
        selectedDate={selectedDate}
        onClearFocus={resetDate}
        scrollRef={rowRef}
        isFocused={isFocused}
      />
    );
  }

  return (
    <>
      <div
        className="w-100 h-100 d-flex flex-column pe-2"
        style={{ backgroundColor: theme.surface, color: theme.text }}
      >
        {/* Header */}
        {!isMobile && <div
          className="d-flex align-items-start justify-content-between px-3 pt-2 pb-2"
          key={"news-feed-title"}
          style={{
            backgroundColor: theme.surface,
            color: theme.text,
          }}
        >
          <div className="d-flex align-items-center">
            <div>
              <div
                className="fw-semibold"
                style={{
                  color: theme.text,
                  fontSize: "1rem",
                  letterSpacing: "-0.01em",
                }}
              >
                Info Flow
              </div>
            </div>

            {selectedPoint && (
              <div
                className="d-flex align-items-center gap-2 ms-2"
                onClick={() => {
                  resetDate();
                  setHover(false);
                }}
              >
                <p
                  className="d-flex align-items-center mb-0 border rounded-pill p-2 position-relative"
                  style={{
                    cursor: "pointer",
                    backgroundColor: theme.pillBg,
                    borderColor: theme.pillBorder,
                    color: theme.text,
                  }}
                  onMouseEnter={() => setHover(true)}
                  onMouseLeave={() => setHover(false)}
                >
                  {!hover ? (
                    <i
                      className="fa-solid fa-newspaper me-2 fs-98px"
                      style={{ color: theme.muted }}
                    />
                  ) : (
                    <i
                      className="fa-solid fa-x me-2 fs-98px"
                      style={{ color: theme.muted }}
                    />
                  )}

                  <span style={{ color: theme.text }}>
                    {capitalize(selectedPoint?.country)}{" "}
                    {selectedPoint?.name}{" "}
                    {selectedDate?.toLocaleDateString("en-US")}
                  </span>
                </p>
              </div>
            )}
          </div>

          <div className="d-flex justify-content-center align-items-center pt-1">
            {newsArticles[0]?.ner_relevance_score && (
              <InfoButton color={theme.muted} />
            )}
          </div>
        </div>}

        {/* Scroll container */}
        <div
          className="py-2 flex-grow-1"
          style={{
            overflow: "auto",
            backgroundColor: theme.surface,
          }}
          key={"news-feed-container"}
          ref={rowRef}
        >
          {isNewsLoading ? (
            <NewsFeedSkeleton />
          ) : selectedNews ? (
            <NewsDetailDiv news={selectedNews} onClose={onClose} isDark={isDark} />
          ) : (
            <>
              {newsArticles.map((news, index) => (
                <NewsItem
                  news={news}
                  key={`${news.news_url}-${index}`}
                  onClick={() =>
                    handleNewsClick(
                      setSelectedNews,
                      news,
                      documentIds,
                      setPrevDocumentIds,
                      setDocumentIds
                    )
                  }
                  isDark={isDark}
                />
              ))}
              {isMoreNewsLoading && <NewsItemSkeleton />}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default NewsFeed;
